import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Competitor } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, DataTable, EmptyState, Icon, LoadingBlock, type Column } from '@/design'
import { formatDateKey } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { ActivitySummaryStrip } from './ActivitySummaryStrip'
import { AddCompetitorForm } from './AddCompetitorForm'
import { competitorColumns } from './columns'
import { CsvImportField } from './CsvImportField'
import { useDrillDown, useImportCompetitorsCsv, useMarketQuery, useMoney } from './hooks'
import { NotLiveSection } from './NotLiveSection'
import './market.css'

function columns(money: ReturnType<typeof useMoney>, selected: Set<string>, toggle: (id: string) => void): Column<Competitor>[] {
  return [
    ...competitorColumns(money),
    { key: 'since', header: 'Tracked since', sortValue: (row) => row.trackedSince, cell: (row) => formatDateKey(row.trackedSince, true) },
    {
      key: 'compare',
      header: 'Compare',
      cell: (row) => (
        <label className="mk-compare-checkbox">
          <input
            type="checkbox"
            checked={selected.has(row.id)}
            onChange={() => toggle(row.id)}
            aria-label={`Select ${row.name} to compare`}
          />
        </label>
      ),
    },
  ]
}

/** Market Intelligence landing: the competitors a tenant tracks, and what has changed lately. */
export function CompetitorsPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketCompetitors')
  const competitors = useMarketQuery('competitors', (client, filters) => client.marketCompetitors(filters))
  const href = useDrillDown()
  const money = useMoney()
  const search = useFilterSearch()
  const navigate = useNavigate()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [showAddForm, setShowAddForm] = useState(false)

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function compareSelected() {
    const ids = [...selected]
    const [first, ...rest] = ids
    if (!first) return
    const params = new URLSearchParams(search.replace(/^\?/, ''))
    if (rest.length > 0) params.set('compare', rest.join(','))
    const query = params.toString()
    navigate(`/market/competitors/${first}${query ? `?${query}` : ''}`)
  }

  if (notLive) return <NotLiveSection route={route} />

  return (
    <SectionFrame
      route={route}
      actions={
        <button type="button" className="ci-button" data-variant="primary" onClick={() => setShowAddForm((current) => !current)}>
          {showAddForm ? 'Close' : 'Add competitor'}
        </button>
      }
    >
      <div className="ci-stack">
        <ActivitySummaryStrip />

        {showAddForm ? (
          <Card title="Add or import competitors">
            <AddCompetitorForm onDone={() => setShowAddForm(false)} />
            <CsvImportBlock />
          </Card>
        ) : null}

        <Card
          title="Competitors"
          subtitle="Every operator you track. Select two or more to compare side by side."
          busy={competitors.isFetching}
          footer={
            <button type="button" className="ci-button" disabled={selected.size < 2} onClick={compareSelected}>
              Compare selected ({selected.size}) <Icon name="arrowRight" size={14} />
            </button>
          }
        >
          {competitors.isPending ? <LoadingBlock rows={6} /> : null}
          {competitors.isError ? <EmptyState variant="outage" compact description="Competitors could not be loaded." /> : null}
          {competitors.data ? (
            <DataTable
              caption="Competitors"
              columns={columns(money, selected, toggle)}
              rows={competitors.data.data}
              rowKey={(row) => row.id}
              rowHref={(row) => href(row.drillDown)}
              initialSort={{ key: 'name', direction: 'asc' }}
              empty={<EmptyState variant="no-data" compact title="No competitors tracked yet" description="Add one manually or import a CSV above." />}
            />
          ) : null}
        </Card>
      </div>
    </SectionFrame>
  )
}

function CsvImportBlock() {
  const importCsv = useImportCompetitorsCsv()
  return (
    <CsvImportField
      label="Or import competitors via CSV"
      header="name,domain,brand,frequency,typicalTicketPrice,prizeCategories"
      onImport={(csv) => importCsv.mutateAsync(csv)}
    />
  )
}
